import React, { useEffect, useRef, useState } from 'react'

import CasesService from '../../../services/CasesService'
import Permissions, { CAPABILITIES } from '../../../utils/Permissions'
import validation from '../../../validation'
import ConfirmationModal from '../../controls/ConfirmationModal'

import ReportPageTemplate from './ReportPageTemplate'

const Report = ({ caseNumber, reportNumber, goToReports, showError, settings, user }) => {
  const [caseReportPromise, setCaseReportPromise] = useState(() => CasesService.getReport(caseNumber, reportNumber))
  const [filesPromise] = useState(() => CasesService.getReportFiles(caseNumber, reportNumber))
  const [confirmApprove, setConfirmApprove] = useState(false)
  const [confirmReject, setConfirmReject] = useState(false)
  const [rejectReason, setRejectReason] = useState()
  const [invoiceNumber, setInvoiceNumber] = useState('')
  const [showRejectReasonValidation, setShowRejectReasonValidation] = useState(false)

  const [confirmInvoice, setConfirmInvoice] = useState(false)
  const [invoiceNumberToSave, setInvoiceNumberToSave] = useState('')
  const [noInvoiceNumber, setNoInvoiceNumber] = useState(false)
  const [noInvoiceNumberReason, setNoInvoiceNumberReason] = useState('')
  const [showInvoiceValidation, setShowInvoiceValidation] = useState(false)
  const invoiceInputRef = useRef(null)
  const reasonInputRef = useRef(null)

  useEffect(() => {
    if (confirmInvoice) {
      if (noInvoiceNumber) {
        reasonInputRef.current?.focus()
      } else {
        invoiceInputRef.current?.focus()
      }
    }
  }, [confirmInvoice, noInvoiceNumber])

  const handleApproveReport = () => {
    CasesService.approveReport(caseNumber, reportNumber, invoiceNumber)
      .onSuccess(r => setCaseReportPromise(Promise.resolve(r)))
      .onBadRequest(err => showError(err))
      .onStatus(401, () => showError('Authentication failed. Please log in again.'))
      .onStatus(403, () => showError('You do not have permission to approve this report.'))
      .onStatus(404, () => showError('Report not found. It may have been deleted or moved.'))
      .onStatus(500, () => showError('Server error occurred. Please try again later.'))
      .catch(err => {
        console.error('Report approval error:', err)
        showError(
          `An unexpected error occurred while ${settings.caseStatuses.approved.continuousVerb.toLowerCase()} the report. Please check your network connection and try again.`,
        )
      })
      .then(() => {
        setConfirmApprove(false)
        setInvoiceNumber('')
      })
  }

  const handleRejectReport = () => {
    if (!rejectReason) {
      setShowRejectReasonValidation(true)
      return
    }

    setShowRejectReasonValidation(false)
    CasesService.rejectReport(caseNumber, reportNumber, rejectReason)
      .onSuccess(r => setCaseReportPromise(Promise.resolve(r)))
      .onBadRequest(err => showError(err))
      .onStatus(401, () => showError('Authentication failed. Please log in again.'))
      .onStatus(403, () => showError('You do not have permission to return this report.'))
      .onStatus(404, () => showError('Report not found. It may have been deleted or moved.'))
      .onStatus(500, () => showError('Server error occurred. Please try again later.'))
      .catch(err => {
        console.error('Report return error:', err)
        showError(
          `An unexpected error occurred while returning the report. Please check your network connection and try again.`,
        )
      })
      .then(() => setConfirmReject(false))
  }

  const handleCloseRejectConfirmation = () => {
    setConfirmReject(false)
    setShowRejectReasonValidation(false)
    setRejectReason(null)
  }

  const handleMarkAsInvoiced = () => {
    if (!noInvoiceNumber && !invoiceNumberToSave.trim()) {
      setShowInvoiceValidation(true)
      return
    }
    if (noInvoiceNumber && !noInvoiceNumberReason.trim()) {
      setShowInvoiceValidation(true)
      return
    }

    setShowInvoiceValidation(false)
    CasesService.markReportInvoiced(caseNumber, reportNumber, {
      invoiceNumber: noInvoiceNumber ? null : invoiceNumberToSave.trim(),
      noInvoiceNumber,
      reason: noInvoiceNumber ? noInvoiceNumberReason.trim() : null,
    })
      .onSuccess(r => setCaseReportPromise(Promise.resolve(r)))
      .onBadRequest(err => showError(err))
      .onStatus(401, () => showError('Authentication failed. Please log in again.'))
      .onStatus(403, () => showError('You do not have permission to invoice this report.'))
      .onStatus(404, () => showError('Report not found. It may have been deleted or moved.'))
      .onStatus(500, () => showError('Server error occurred. Please try again later.'))
      .catch(err => {
        console.error('Report invoice error:', err)
        showError('An unexpected error occurred while marking the report as invoiced.')
      })
      .then(() => {
        setConfirmInvoice(false)
        setInvoiceNumberToSave('')
        setNoInvoiceNumber(false)
        setNoInvoiceNumberReason('')
        setShowInvoiceValidation(false)
      })
  }

  const handleCloseInvoiceConfirmation = () => {
    setConfirmInvoice(false)
    setInvoiceNumberToSave('')
    setNoInvoiceNumber(false)
    setNoInvoiceNumberReason('')
    setShowInvoiceValidation(false)
  }

  const AdminActions = ({ reportModel }) => {
    const canApproveOrReturn =
      Permissions.can(user, CAPABILITIES.APPROVE_REPORT) || Permissions.can(user, CAPABILITIES.RETURN_REPORT)
    const canInvoice = Permissions.can(user, CAPABILITIES.UPDATE_CASE) || canApproveOrReturn

    const isApprovedAwaitingInvoice =
      reportModel.status === 'approved' ||
      (reportModel.approvedOnUtc &&
        reportModel.status !== 'invoiced' &&
        !reportModel.rejectedOnUtc &&
        !reportModel.canceledOnUtc)

    if (isApprovedAwaitingInvoice && canInvoice) {
      return (
        <div>
          <button
            id="case-report-invoice-btn"
            type="button"
            className="btn btn-primary"
            onClick={() => setConfirmInvoice(true)}
          >
            Mark as Invoiced
          </button>
        </div>
      )
    }

    if (!canApproveOrReturn) return null
    if (
      !reportModel.submittedOnUtc ||
      reportModel.approvedOnUtc ||
      reportModel.rejectedOnUtc ||
      reportModel.canceledOnUtc
    )
      return null
    return (
      <div>
        <button
          id="case-report-reject-btn"
          type="button"
          className="mr-1 btn btn-outline-primary"
          onClick={() => setConfirmReject(true)}
        >
          Return to Submitter
        </button>
        <button
          id="case-report-approve-btn"
          type="button"
          className="btn btn-primary"
          onClick={() => setConfirmApprove(true)}
        >
          {settings.caseStatuses.approved.presentTenseVerb}
        </button>
      </div>
    )
  }

  return (
    <>
      <ReportPageTemplate
        caseNumber={caseNumber}
        isInProgress={false}
        user={user}
        caseReportPromise={caseReportPromise}
        filesPromise={filesPromise}
        settings={settings}
        goToReports={goToReports}
        actionsComponent={AdminActions}
        printableReportPath={`/cases/${caseNumber}/reports/${reportNumber}/print`}
      />

      {confirmReject && (
        <ConfirmationModal
          title="Return Report to Submitter"
          body={
            <>
              <div>
                Are you sure you want to return this report to the submitter? The status will be changed to In Progress
                to allow edits.
              </div>

              <div className="mt-3 form-group">
                <label htmlFor="rejectReason" className="form-label">
                  Reason for return
                </label>
                <textarea
                  id="rejectReason"
                  className="form-control"
                  maxLength={validation.maxLengths.shortText}
                  value={rejectReason ?? ''}
                  onChange={e => setRejectReason(e.target.value)}
                  onBlur={() => setShowRejectReasonValidation(true)}
                />
                {!showRejectReasonValidation || rejectReason ? null : (
                  <div className="form-text text-danger">Reason for return is required</div>
                )}
              </div>
            </>
          }
          onConfirm={handleRejectReport}
          onClose={handleCloseRejectConfirmation}
        />
      )}
      {confirmApprove && (
        <ConfirmationModal
          title={`${settings.caseStatuses.approved.presentTenseVerb} Report`}
          body={
            <>
              <div>
                Are you sure you want to {settings.caseStatuses.approved.presentTenseVerb.toLowerCase()} this report?
                This action cannot be undone.
              </div>
              <div className="mt-3 form-group">
                <label htmlFor="invoiceNumber" className="form-label">
                  Invoice # (Optional)
                </label>
                <input
                  id="invoiceNumber"
                  type="text"
                  className="form-control"
                  maxLength={50}
                  value={invoiceNumber}
                  onChange={e => setInvoiceNumber(e.target.value)}
                />
              </div>
            </>
          }
          onConfirm={handleApproveReport}
          onClose={() => setConfirmApprove(false)}
        />
      )}
      {confirmInvoice && (
        <ConfirmationModal
          title="Mark as Invoiced"
          body={
            <>
              <div>Enter the AR invoice number to complete invoicing and finalize this report.</div>

              <div className="mt-3 form-group">
                <label htmlFor="invoiceNumberModal" className="form-label font-weight-bold">
                  Invoice # (from AR)
                </label>
                <input
                  ref={invoiceInputRef}
                  id="invoiceNumberModal"
                  type="text"
                  className="form-control"
                  maxLength={50}
                  placeholder="e.g. INV-10492"
                  disabled={noInvoiceNumber}
                  value={invoiceNumberToSave}
                  onChange={e => setInvoiceNumberToSave(e.target.value)}
                />
                {showInvoiceValidation && !noInvoiceNumber && !invoiceNumberToSave.trim() && (
                  <div className="form-text text-danger">
                    Invoice number is required, or check &quot;No invoice number for this report&quot;.
                  </div>
                )}
              </div>

              <div className="form-check mt-3">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="noInvoiceNumberCheckbox"
                  checked={noInvoiceNumber}
                  onChange={e => {
                    setNoInvoiceNumber(e.target.checked)
                    setShowInvoiceValidation(false)
                  }}
                />
                <label className="form-check-label" htmlFor="noInvoiceNumberCheckbox">
                  No invoice number for this report
                </label>
              </div>

              {noInvoiceNumber && (
                <div className="mt-3 form-group">
                  <label htmlFor="noInvoiceReason" className="form-label font-weight-bold">
                    Reason for no invoice number
                  </label>
                  <textarea
                    ref={reasonInputRef}
                    id="noInvoiceReason"
                    className="form-control"
                    rows={3}
                    maxLength={255}
                    placeholder="Enter reason (e.g. Billed under monthly retainer agreement, Pro bono, Internal review)"
                    value={noInvoiceNumberReason}
                    onChange={e => setNoInvoiceNumberReason(e.target.value)}
                  />
                  {showInvoiceValidation && !noInvoiceNumberReason.trim() && (
                    <div className="form-text text-danger">Reason is required when no invoice number is provided.</div>
                  )}
                </div>
              )}
            </>
          }
          confirmButtonText="Mark as Invoiced"
          onConfirm={handleMarkAsInvoiced}
          onClose={handleCloseInvoiceConfirmation}
        />
      )}
    </>
  )
}

export default Report
